/**
 * Heuro Cinema - Card System
 * Handles card creation, animation, docking, and expansion.
 */

import type { DockPosition, CardContent } from '../types/content';

interface CardElement extends HTMLElement {
  dockPosition: DockPosition;
  cardData: CardContent;
  pillarAccent: string;
}

const createdCards: CardElement[] = [];
let expandedCard: CardElement | null = null;

export function getCreatedCards(): CardElement[] {
  return createdCards;
}

export function updateCardContent(pillarId: string, index: number, data: CardContent): void {
  const card = createdCards.find(
    c => c.dataset.pillar === pillarId && c.dataset.index === String(index)
  );
  if (!card) return;

  // Update stored data
  card.cardData = data;

  // Update visible content (only if not expanded)
  if (!card.classList.contains('expanded')) {
    const titleHtml = data.title.replace(/\n/g, '<br>');
    const front = card.querySelector('.card-front');
    if (front) {
      front.innerHTML = `
        <span class="card-tag">${data.tag}</span>
        <h2 class="card-title">${titleHtml}</h2>
        <p class="card-insight">${data.insight}</p>
      `;
    }
  }
}

export function createCard(
  data: CardContent,
  pillarId: string,
  pillarAccent: string,
  isMain: boolean,
  index: number,
  dockPosition: DockPosition
): CardElement {
  const container = document.getElementById('cardsContainer');
  if (!container) throw new Error('Cards container not found');

  const card = document.createElement('div') as unknown as CardElement;
  card.className = `card ${isMain ? 'main-card' : 'split-card'}`;
  card.dataset.pillar = pillarId;
  card.dataset.index = String(index);
  card.dockPosition = dockPosition;
  card.cardData = data;
  card.pillarAccent = pillarAccent;

  // Convert newlines to <br> for display
  const titleHtml = data.title.replace(/\n/g, '<br>');

  card.innerHTML = `
    <div class="card-face card-back"></div>
    <div class="card-face card-front">
      <span class="card-tag">${data.tag}</span>
      <h2 class="card-title">${titleHtml}</h2>
      <p class="card-insight">${data.insight}</p>
    </div>
  `;

  card.addEventListener('click', (e) => {
    e.stopPropagation();
    if (card.classList.contains('docked') && !card.classList.contains('expanded')) {
      expandCard(card);
    } else if (card.classList.contains('expanded')) {
      collapseCard(card);
    }
  });

  container.appendChild(card);
  createdCards.push(card);

  return card;
}

function renderExpandedContent(card: CardElement): void {
  const front = card.querySelector('.card-front');
  if (!front) return;

  const data = card.cardData;
  const detail = data.detail;

  if (!detail) {
    // No detail content, just show summary centered
    return;
  }

  // Build benefits HTML
  const benefitsHtml = detail.benefits
    .map(b => `<li>${b}</li>`)
    .join('');

  // Store original content and replace with expanded view
  const originalContent = front.innerHTML;
  front.setAttribute('data-original-content', originalContent);

  front.innerHTML = `
    <div class="card-expanded-content">
      <span class="card-tag">${data.tag}</span>
      <h2 class="card-headline">${detail.headline}</h2>
      <p class="card-body">${detail.body}</p>
      <ul class="card-benefits">
        ${benefitsHtml}
      </ul>
    </div>
  `;

  front.classList.add('expanded-view');
}

function restoreCollapsedContent(card: CardElement): void {
  const front = card.querySelector('.card-front');
  if (!front) return;

  const originalContent = front.getAttribute('data-original-content');
  if (originalContent) {
    front.innerHTML = originalContent;
    front.removeAttribute('data-original-content');
    front.classList.remove('expanded-view');
  }
}

function isMobile(): boolean {
  return window.innerWidth < 768;
}

function expandCard(card: CardElement): void {
  if (expandedCard) {
    collapseCard(expandedCard);
  }

  expandedCard = card;
  card.classList.add('expanded');
  card.classList.remove('docked');

  // Render expanded content
  renderExpandedContent(card);

  const mobile = isMobile();
  const hasDetail = !!card.cardData.detail;
  const targetWidth = mobile ? Math.min(window.innerWidth - 32, 400) : (hasDetail ? 480 : 320);
  const targetHeight = hasDetail ? 'auto' : (mobile ? 'auto' : 200);

  card.style.top = '50%';
  card.style.left = '50%';
  card.style.right = 'auto';
  card.style.bottom = 'auto';
  card.style.width = targetWidth + 'px';
  card.style.opacity = '1';
  if (targetHeight !== 'auto') {
    card.style.height = targetHeight + 'px';
  } else {
    card.style.height = 'auto';
    card.style.maxHeight = mobile ? '80vh' : '70vh';
  }
  card.style.transform = 'translate(-50%, -50%) scale(1)';

  // Show scrim and hide CTA
  const scrim = document.getElementById('cardScrim');
  const ctaStage = document.getElementById('ctaStage');
  scrim?.classList.add('visible');
  ctaStage?.classList.add('card-expanded-hide');

  // On mobile, scroll to top to ensure card is visible
  if (mobile) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

export function collapseCard(card?: CardElement | null): void {
  const cardToCollapse = card || expandedCard;
  if (!cardToCollapse) return;

  // Restore original content
  restoreCollapsedContent(cardToCollapse);

  cardToCollapse.classList.remove('expanded');
  cardToCollapse.classList.add('docked');

  const mobile = isMobile() && document.body.classList.contains('mobile-docked');

  // Reset size — on mobile docked mode, CSS handles sizing
  if (!mobile) {
    const isMain = cardToCollapse.classList.contains('main-card');
    cardToCollapse.style.width = isMain ? '280px' : '240px';
    cardToCollapse.style.height = isMain ? '170px' : '150px';
    applyDockPosition(cardToCollapse);
  } else {
    cardToCollapse.style.width = '';
    cardToCollapse.style.height = '';
    cardToCollapse.style.top = '';
    cardToCollapse.style.left = '';
    cardToCollapse.style.right = '';
    cardToCollapse.style.bottom = '';
    cardToCollapse.style.transform = '';
  }
  cardToCollapse.style.maxHeight = '';
  cardToCollapse.style.opacity = '1';

  expandedCard = null;

  // Hide scrim and show CTA again
  const scrim = document.getElementById('cardScrim');
  const ctaStage = document.getElementById('ctaStage');
  scrim?.classList.remove('visible');
  ctaStage?.classList.remove('card-expanded-hide');
}

function applyDockPosition(card: CardElement): void {
  const pos = card.dockPosition;

  card.style.top = 'auto';
  card.style.left = 'auto';
  card.style.right = 'auto';
  card.style.bottom = 'auto';

  if (pos.top !== undefined) {
    card.style.top = typeof pos.top === 'number' ? pos.top + 'px' : pos.top;
  }
  if (pos.left !== undefined) {
    card.style.left = typeof pos.left === 'number' ? pos.left + 'px' : pos.left;
  }
  if (pos.right !== undefined) {
    card.style.right = typeof pos.right === 'number' ? pos.right + 'px' : pos.right;
  }
  if (pos.bottom !== undefined) {
    card.style.bottom = typeof pos.bottom === 'number' ? pos.bottom + 'px' : pos.bottom;
  }

  card.style.transform = `scale(${pos.scale})`;
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Get timing from CSS variables (set by API config)
function getTimingMs(varName: string, defaultMs: number): number {
  const style = getComputedStyle(document.documentElement);
  const value = style.getPropertyValue(varName).trim();
  if (!value) return defaultMs;
  // Parse "800ms" or "0.8s"
  const match = value.match(/^([\d.]+)(ms|s)?$/);
  if (!match) return defaultMs;
  const num = parseFloat(match[1]);
  const unit = match[2] || 'ms';
  return unit === 's' ? num * 1000 : num;
}

export async function showCard(card: CardElement): Promise<void> {
  const enterDuration = getTimingMs('--card-enter-duration', 800);
  const flipDelay = getTimingMs('--card-flip-delay', 1000);

  // Start off-screen bottom
  card.style.top = '120%';
  card.style.left = '50%';
  card.style.transform = 'translate(-50%, 0) rotateY(180deg)';
  card.style.opacity = '1';
  card.classList.add('active');

  // Animate to center (enter animation)
  await new Promise<void>(resolve => {
    requestAnimationFrame(() => {
      card.style.top = '50%';
      card.style.transform = 'translate(-50%, -50%) rotateY(180deg)';

      // Wait for enter animation, then flip
      setTimeout(() => {
        // Flip
        card.style.transform = 'translate(-50%, -50%) rotateY(0deg)';
        // Wait for flip to complete (proportional to enter duration)
        setTimeout(resolve, enterDuration * 0.6);
      }, flipDelay);
    });
  });
}

export async function dockCard(card: CardElement): Promise<void> {
  const dockDuration = getTimingMs('--card-dock-duration', 600);
  card.classList.remove('active');
  card.classList.add('docked');
  applyDockPosition(card);
  await wait(dockDuration);
}

export async function splitAndDock(
  mainCard: CardElement,
  splitCards: CardElement[]
): Promise<void> {
  const dockDuration = getTimingMs('--card-dock-duration', 600);
  const flipDelay = getTimingMs('--card-flip-delay', 1000);
  // Scale split timings based on dock duration ratio
  const ratio = dockDuration / 600;

  // Dock main first
  await dockCard(mainCard);

  // Then each split
  for (const split of splitCards) {
    split.style.top = '50%';
    split.style.left = '50%';
    split.style.transform = 'translate(-50%, -50%) rotateY(180deg) scale(0.8)';
    split.style.opacity = '1';
    split.classList.add('active');

    await wait(150 * ratio);
    split.style.transform = 'translate(-50%, -50%) rotateY(180deg) scale(1)';

    await wait(200 * ratio);
    // Flip
    split.style.transform = 'translate(-50%, -50%) rotateY(0deg) scale(1)';

    await wait(Math.min(flipDelay * 0.4, 400 * ratio));
    // Dock
    split.classList.remove('active');
    split.classList.add('docked');
    applyDockPosition(split);

    await wait(250 * ratio);
  }
}

export function setupCardScrimListener(): void {
  const scrim = document.getElementById('cardScrim');
  scrim?.addEventListener('click', () => {
    if (expandedCard) {
      collapseCard(expandedCard);
    }
  });
}

export function hasExpandedCard(): boolean {
  return expandedCard !== null;
}